import React, { useState, useEffect } from 'react'

import styled from 'styled-components'

import Modal from 'components/Modal'
import { Button } from '@nexiahome/ui-common/components/primitives/buttons'

import { shortTimeDate } from 'helpers/formatters'

import { color } from 'styles/colors'
import { ButtonContainer, screenSize } from 'styles/shared'

import { Status } from 'types/RemoteConfig'
import { DeviceBase } from 'types/Device'

import Api from 'services/api'

import fetchCognitoToken from 'helpers/aws'

type Props = {
  status: Status
  device: DeviceBase
}

const RequestRemoteStatus = ({ status, device }: Props) => {
  const [showDRCContanier, setShowDRCContainer] = useState(false)
  const [buttonDisabled, setButtonDisabled] = useState(false)
  const [idToken, setIdToken] = useState<string>('')

  const onStopRemoteConfig = async () => {
    setButtonDisabled(true)

    await Api.RemoteConfig.stop(device.deviceId)
  }

  useEffect(() => {
    const fetchData = async () => {
      const idToken = await fetchCognitoToken()

      if (idToken) {
        setIdToken(idToken.toString())
      }
    }

    fetchData()
  }, [])

  const requestState = (state) => {
    switch (state) {
      case 'pending':
        return 'REMOTE ACCESS PENDING'
      case 'active':
        return 'REMOTE SESSION ACTIVE'
      case 'accepted':
        return 'REMOTE ACCESS GRANTED'
      case 'denied':
        return 'REMOTE ACCESS DENIED'
      case 'expired':
        return 'REMOTE SESSION EXPIRED'
      case 'stopped':
        return 'REMOTE SESSION STOPPED'
    }
  }

  const message = (status: Status) => {
    switch (status.state) {
      case 'pending':
        return 'Remote access requested ' + shortTimeDate(status.requestedAt)
      case 'active':
        return 'Expires at ' + shortTimeDate(status.expiresAt)
      case 'accepted':
        return 'Expires at ' + shortTimeDate(status.expiresAt)
      case 'denied':
        return 'Remote access denied at ' + shortTimeDate(status.deniedAt)
      case 'expired':
        return 'Expired at ' + shortTimeDate(status.expiresAt)
      case 'stopped':
        return 'Stopped at ' + shortTimeDate(status.stoppedAt)
    }
  }

  const rightButtonName = (state) => {
    switch (state) {
      case 'pending':
        return 'Cancel'
      case 'accepted':
      case 'active':
        return 'Done'
      case 'denied':
      case 'expired':
      case 'stopped':
        return 'Close'
    }
  }

  const isValidState = ['accepted', 'active', 'pending', 'denied', 'expired', 'stopped'].includes(status.state ?? '')

  const isControlHub = device.deviceType === 'control_hub'

  return (
    <S.RequestRemoteStatus>
      {
        isValidState && (
          <S.Status>
            <S.Header>{requestState(status.state)}</S.Header>
            <S.Message>{message(status)}</S.Message>
            <S.ButtonContainer>
              {!isControlHub && ['accepted', 'active'].includes(status.state) && <Button decoration="textOnly" text="Start" onPress={() => setShowDRCContainer(true)} />}
              {!isControlHub && <Button decoration="textOnly" enabled={!buttonDisabled} text={rightButtonName(status.state) || ''} onPress={onStopRemoteConfig} />}
              {isControlHub && <S.LinkDeviceInfo>Use the Technician App to access a Remote Session for this device.</S.LinkDeviceInfo>}
            </S.ButtonContainer>
          </S.Status>
        )
      }
      {showDRCContanier && (
        <Modal isDisplayed closeCallback={() => setShowDRCContainer(false)}>
          <div>
            <S.Iframe src={Api.RemoteConfig.iframeUrl(device.deviceId, idToken)} title="DRC" aria-label="drc session" />
            <ButtonContainer noBorder>
              <Button decoration="positive" text="Close" onPress={() => setShowDRCContainer(false)} />
            </ButtonContainer>
          </div>
        </Modal>
      )}
    </S.RequestRemoteStatus>
  )
}

export default RequestRemoteStatus

const S = {
  RequestRemoteStatus: styled.div`
    margin-right: 15px;
  `,
  Status: styled.div`
    border: 1px solid ${color('border')};
    border-radius: 4px;
    padding: 10px 10px 0px 10px;
    margin-bottom: 10px;
    width: 185px;
    font-weight: 700;
    font-size: 1rem;

    @media ${screenSize.small} {
      width: 100%;
    }
  `,
  Header: styled.div`
    text-transform: uppercase;
  `,
  Message: styled.div`
    color: ${color('secondaryText')}
  `,
  ButtonContainer: styled.div`
    margin-top: 15px;
    display: flex;
    justify-content: space-between;
  `,
  LinkDeviceInfo: styled.div`
    @media ${screenSize.small} {
      margin-bottom: 15px;
    }
  `,
  Iframe: styled.iframe`
    min-width: 804px;
    min-height: 515px;
    width: 100%;
    height: 100%;
    border: none;
  `
}
